import React, { useState, useRef, useEffect } from 'react';
import './ProfileDropdown.css';
import { useAuth } from '../hooks/useAuth';
import TwoFactorSetup from './TwoFactorSetup';
import { useTheme } from '../contexts/ThemeContext';

const ProfileDropdown = ({ onSupportClick, onOpenProfile }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTwoFactorOpen, setIsTwoFactorOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Check if 2FA is enabled
  const is2FAEnabled = localStorage.getItem('2fa_enabled') === 'true';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuItemClick = async (action) => {
    setIsOpen(false);
    switch(action) {
      case 'support':
        onSupportClick();
        break;
      case 'logout':
        await logout();
        break;
      case 'profile':
        if (onOpenProfile) onOpenProfile();
        break;
      case 'settings':
        alert('Settings page would open here');
        break;
      case 'admin':
        alert('Admin panel would open here');
        break;
      case 'notifications':
        alert('Notifications panel would open here');
        break;
      case '2fa':
        setIsTwoFactorOpen(true);
        break;
      default:
        break;
    }
  };

  // If no user data available, don't render
  if (!user) return null;

  return (
    <div className="profile-dropdown" ref={dropdownRef}>
      <button 
        className="profile-button"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="user-avatar">
          {user.picture ? (
            <img src={user.picture} alt={user.name} />
          ) : (
            user.initials
          )}
        </div>
        <span className="user-name">{user.name.split(' ')[0]}</span>
        <svg 
          className={`chevron ${isOpen ? 'open' : ''}`} 
          width="12" 
          height="12" 
          viewBox="0 0 12 12"
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="2" fill="none"/>
        </svg>
      </button>

      {isOpen && (
        <div className="dropdown-menu">
          <div className="dropdown-header">
            <div className="user-info">
              <div className="user-avatar large">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} />
                ) : (
                  user.initials
                )}
              </div>
              <div>
                <div className="user-full-name">{user.name}</div>
                <div className="user-email">{user.email}</div>
              </div>
            </div>
          </div>
          
          <div className="dropdown-divider"></div>
          
          <button 
            className="dropdown-item"
            onClick={() => handleMenuItemClick('profile')}
          >
            <svg className="menu-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
            My Profile
          </button>
          
          <button 
            className="dropdown-item"
            onClick={() => handleMenuItemClick('settings')}
          >
            <svg className="menu-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
            </svg>
            Settings
          </button>
          
          <button 
            className="dropdown-item"
            onClick={() => handleMenuItemClick('notifications')}
          >
            <svg className="menu-icon" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
            </svg>
            Notifications
            <span className="notification-badge">3</span>
          </button>
          
          <div className="dropdown-divider"></div>
          
          <button 
            className="dropdown-item"
            onClick={() => handleMenuItemClick('admin')}
          >
            <svg className="menu-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Admin Panel
          </button>
          
          <button 
            className="dropdown-item"
            onClick={() => handleMenuItemClick('2fa')}
          >
            <svg className="menu-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            {is2FAEnabled ? (
              <>
                <span className="twofa-status-enabled">2FA Enabled</span>
                <svg className="twofa-check-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </>
            ) : (
              'Setup 2FA'
            )}
          </button>
          
          <button 
            className="dropdown-item support"
            onClick={() => handleMenuItemClick('support')}
          >
            <svg className="menu-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            Support Center
          </button>

          <div className="dropdown-footer">
            <button className="theme-toggle" onClick={toggleTheme}>
              <span className="theme-dot" />
              <span>{theme === 'dark' ? 'Dark mode' : 'Light mode'}</span>
            </button>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Theme</span>
          </div>
          
          <div className="dropdown-divider"></div>
          
          <button 
            className="dropdown-item logout"
            onClick={() => handleMenuItemClick('logout')}
          >
            <svg className="menu-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
            </svg>
            Logout
          </button>
        </div>
      )}
      
      <TwoFactorSetup
        isOpen={isTwoFactorOpen}
        onClose={() => setIsTwoFactorOpen(false)}
        onComplete={() => {
          setIsOpen(false);
        }}
      />
    </div>
  );
};

export default ProfileDropdown;